export interface NavItem {
  to: string;
  label: string;
  icon: string; // sprite id without the "i-" prefix
  resource?: string;
  adv?: boolean;
  count?: string; // optional demo/live count badge
  superadminOnly?: boolean; // only visible to superadmins
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

// Grouped navigation for the application-grade shell. Order + grouping match the
// approved prototype. `resource` drives permission gating (superadmin sees all).
export const navGroups: NavGroup[] = [
  {
    label: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: "grid", resource: "dashboard" }],
  },
  {
    label: "Knowledge & Content",
    items: [
      { to: "/content", label: "Content Library", icon: "db", resource: "content_library" },
      { to: "/help", label: "Help Center", icon: "help", resource: "help_center" },
      { to: "/news", label: "Newsroom", icon: "news", resource: "newsroom" },
      { to: "/authors", label: "Authors", icon: "user", resource: "authors" },
      { to: "/media", label: "Media Library", icon: "img", resource: "media_library" },
    ],
  },
  {
    label: "AI Configuration",
    items: [
      { to: "/widget", label: "Widget", icon: "chat", resource: "widget_settings" },
      { to: "/prompt", label: "System Prompt", icon: "txt", resource: "models_settings" },
      // Models (/models), Tools (/tools), and Playground (/playground) are intentionally
      // hidden from the nav — matching the original app. Their routes still exist in
      // App.tsx and are reachable by direct URL, but they're kept off the menu.
    ],
  },
  {
    label: "Content Generation",
    items: [
      { to: "/content-templates", label: "Templates", icon: "tmpl", resource: "content_templates" },
      { to: "/content-test", label: "Test Run", icon: "flask", resource: "content_generation" },
      { to: "/generation-logs", label: "Gen Logs", icon: "list", resource: "generation_logs" },
      { to: "/brand-voice", label: "Brand Voice", icon: "mic", resource: "brand_voice" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/users", label: "Users", icon: "users", resource: "users_management" },
      { to: "/roles", label: "Roles", icon: "shield", resource: "roles_management" },
      { to: "/conversations", label: "Conversations", icon: "msg", resource: "conversations" },
      { to: "/ai-cost", label: "AI Cost", icon: "zap", superadminOnly: true },
      // Audit rows carry member message previews, and the API gates
      // /admin/audit-logs behind ADMIN_ONLY with no dedicated RBAC resource —
      // so this stays superadmin-only rather than inventing a resource key
      // the backend does not know about.
      { to: "/audit-logs", label: "Audit Log", icon: "shield", superadminOnly: true },
      // Findings expose member ids + payout amounts; the API is
      // superadmin-gated end to end, so the nav entry matches.
      { to: "/comp-health", label: "Comp Health", icon: "act", superadminOnly: true },
    ],
  },
];

// Flat lookup for breadcrumbs: pathname -> [groupLabel, itemLabel].
export const crumbForPath = (pathname: string): [string, string] => {
  for (const group of navGroups) {
    for (const item of group.items) {
      if (item.to === pathname) return [group.label, item.label];
    }
  }
  // Sub-routes / editors + nav-hidden pages reachable by direct URL.
  if (pathname.startsWith("/content-templates")) return ["Content Generation", "Edit template"];
  if (pathname === "/models") return ["AI Configuration", "Models"];
  if (pathname === "/tools") return ["AI Configuration", "Tools"];
  if (pathname === "/playground") return ["AI Configuration", "Playground"];
  if (pathname === "/ai-cost") return ["Administration", "AI Cost"];
  if (pathname === "/audit-logs") return ["Administration", "Audit Log"];
  if (pathname === "/comp-health") return ["Administration", "Comp Health"];
  return ["", ""];
};
